import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { getLocalisedDayLabel, computeStayDateRange } from 'tripPlanner/utils/chatDates'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import clsx from 'clsx'
import ItineraryItemCard from './ItineraryItemCard'
import ItineraryContent, { ItinerarySummaryRow, getPreviewImages } from './ItineraryContent'
import Divider from 'LuxKit/Divider'
import { groupBy } from 'lib/array/arrayUtils'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { useDeleteDayMutation, useDeleteItemMutation } from 'tripPlanner/hooks/api/chat'
import ChatItemDropdown from './ChatItemDropdown'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import AssistChip from 'LuxKit/Chips/AssistChip'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import ChatOfferDetailDrawer from './ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from './ChatExperienceDetailDrawer'
import type { DrawerHotel } from './HotelListingDrawer'
import type { OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import AddHotelToTripModal from 'tripPlanner/components/AddToTrip/AddHotelToTripModal'
import { STOP_VISIT_ROLE } from 'tripPlanner/api/types'
import type { StopVisitRole, TripItineraryData, TripStop, TripDay, TripItineraryItem, TripItemWithoutDay, ExperienceMapViewData } from 'tripPlanner/api/types'
import moment from 'moment'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'

const VALID_ROLES = new Set<string>(Object.values(STOP_VISIT_ROLE))

function toStopVisitRole(value: string | undefined): StopVisitRole | undefined {
  if (value !== undefined && VALID_ROLES.has(value)) {
    return value as StopVisitRole
  }
  return undefined
}

const ITEM_CARD_GAP = 12

const PaddedContainer = styled(Sheet)`
  width: 100%;

`

export const DayPillsScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const ScrollIndicatorWrapper = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding-bottom: ${rem(4)};
  width: 100%;
`

const ScrollFade = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  z-index: 1;

  &.left {
    left: 0;
    padding-right: ${rem(4)};
    background: linear-gradient(to left, transparent, ${props => props.theme.palette.neutral.default.eight} 40%);
  }

  &.right {
    right: 0;
    padding-left: ${rem(4)};
    background: linear-gradient(to right, transparent, ${props => props.theme.palette.neutral.default.eight} 40%);
  }
`

export const DayChip = styled(AssistChip)`
  flex-shrink: 0;

  &.isSelected {
    background-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.eight};
    box-shadow: none;
  }
`

const FadeContainer = styled.div`
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const SummaryList = styled.div`
  width: 100%;
`

const SummaryDivider = styled(Divider)`
  width: 100%;
`

const AskAiChip = styled(AssistChip)`
  position: relative;
  box-shadow: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: ${rem(2)};
    background: linear-gradient(
      135deg,
      ${props => props.theme.palette.product.ai.light} 0%,
      ${props => props.theme.palette.product.ai.dark} 50%,
      ${props => props.theme.palette.product.ai.light} 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`

interface Props {
  data?: TripItineraryData
  stopId?: number
  onDayDeleted?: () => void
  onItemDeleted?: () => void
  onSendMessage?: (message: string) => void
  onShowMap?: (hotel: DrawerHotel) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  scrollTargetDayId?: number
}

interface SelectedPlace {
  googlePlaceId: string
  placeName: string
  itemId: number
}

type TimePeriod = 'morning' | 'afternoon' | 'evening'

function getTimePeriod(timeString?: string): TimePeriod {
  if (!timeString) {
    return 'morning'
  }
  const hours = moment(timeString, 'HH:mm').hours()
  if (hours < 12) {
    return 'morning'
  }
  if (hours < 18) {
    return 'afternoon'
  }
  return 'evening'
}

// --- Render helpers ---

interface ItemCardListProps {
  items: Array<TripItineraryItem | TripItemWithoutDay>
  dayDate?: string
  onDelete: (itemId: number) => void
  onClick?: (googlePlaceId: string, placeName: string, itemId: number) => void
  onViewOfferDetails: (item: TripItineraryItem | TripItemWithoutDay) => void
  onAddToTrip?: (item: TripItineraryItem | TripItemWithoutDay) => void
}

function getStayDateRange(item: TripItineraryItem | TripItemWithoutDay, dayDate?: string): string | undefined {
  if (!dayDate) return undefined
  const dayOfStay = 'dayOfStay' in item ? item.dayOfStay : undefined
  const totalDays = 'totalDays' in item ? item.totalDays : undefined
  if (!dayOfStay || !totalDays) return undefined
  return computeStayDateRange(dayDate, dayOfStay, totalDays)
}

function ItemCardList({ items, dayDate, onDelete, onClick, onViewOfferDetails, onAddToTrip }: ItemCardListProps) {
  return (
    <>
      {items.map((item) => (
        <div key={item.id} data-item-id={item.id}>
          <ItineraryItemCard
            item={item}
            role={toStopVisitRole('role' in item ? item.role : undefined)}
            isCheckIn={'isCheckIn' in item ? item.isCheckIn : undefined}
            dayOfStay={'dayOfStay' in item ? item.dayOfStay : undefined}
            totalDays={'totalDays' in item ? item.totalDays : undefined}
            stayDateRange={getStayDateRange(item, dayDate)}
            onDelete={onDelete}
            onClick={onClick}
            onViewOfferDetails={onViewOfferDetails}
            onAddToTrip={onAddToTrip}
          />
        </div>
      ))}
    </>
  )
}

const SCROLL_THRESHOLD = 8

function useScrollOverflow(scrollRef: React.RefObject<HTMLDivElement | null>) {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > SCROLL_THRESHOLD)
    setCanScrollRight(el.scrollWidth - el.scrollLeft - el.clientWidth > SCROLL_THRESHOLD)
  }, [scrollRef])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    checkOverflow()
    el.addEventListener('scroll', checkOverflow, { passive: true })
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', checkOverflow)
      observer.disconnect()
    }
  }, [scrollRef, checkOverflow])

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })
  }, [scrollRef])

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })
  }, [scrollRef])

  return { canScrollLeft, canScrollRight, scrollLeft, scrollRight, onContentChange: checkOverflow }
}

function DayItineraryCanvas(props: Props) {
  const intl = useIntl()
  const { data, stopId, onDayDeleted, onItemDeleted, onSendMessage, onShowMap, onShowExperienceOnMap, onAskAI, scrollTargetDayId } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const dayPillsScrollRef = useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight, onContentChange: recheckDayPillsOverflow } = useScrollOverflow(dayPillsScrollRef)

  const deleteDayMutation = useDeleteDayMutation()
  const deleteItemMutation = useDeleteItemMutation()

  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | undefined>(undefined)
  const [selectedOffer, setSelectedOffer] = useState<{
    offerId: string
    accommodationPropertyId?: string
    offerType?: string
    searchParams?: OfferClickSearchParams
  } | null>(null)

  const handleItemClick = useCallback((googlePlaceId: string, placeName: string, itemId: number) => {
    setSelectedPlace({ googlePlaceId, placeName, itemId })
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedPlace(undefined)
  }, [])

  const handleViewOfferDetails = useCallback((item: TripItineraryItem | TripItemWithoutDay) => {
    const savedData = item.savedItemData
    if (savedData?.source === 'offer_experience') {
      setSelectedOffer({
        offerId: savedData.experienceId,
        offerType: 'experience',
      })
      return
    }
    const offerData = isHotelSavedData(savedData) ? savedData : undefined
    if (!offerData) return
    const searchParams: OfferClickSearchParams = {}
    const accommodationPropertyId = (
      'accommodationPropertyId' in offerData && typeof offerData.accommodationPropertyId === 'string'
    ) ? offerData.accommodationPropertyId : undefined
    if (offerData.checkIn) searchParams.checkIn = offerData.checkIn
    if (offerData.checkOut) searchParams.checkOut = offerData.checkOut
    setSelectedOffer({
      offerId: offerData.offerId,
      accommodationPropertyId,
      offerType: offerData.offerType,
      searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
    })
  }, [])

  const handleCloseHotelDrawer = useCallback(() => {
    setSelectedOffer(null)
  }, [])

  const handleShowExperienceOnMap = useCallback((experience: { offerId: string; name: string; geoLocation?: { lat: number; lng: number } }) => {
    setSelectedOffer(null)
    if (!experience.geoLocation) return
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [onShowExperienceOnMap])

  const [addToTripItem, setAddToTripItem] = useState<{ name: string } | undefined>(undefined)

  const handleAddToTrip = useCallback((item: TripItineraryItem | TripItemWithoutDay) => {
    setAddToTripItem({ name: item.name })
  }, [])

  const handleAddToTripModalClose = useCallback(() => {
    setAddToTripItem(undefined)
  }, [])

  const handleAddToTripSubmit = useCallback((message: string) => {
    onSendMessage?.(message)
    setAddToTripItem(undefined)
  }, [onSendMessage])

  const stop = useMemo(() => {
    if (!stopId || !data) {
      return undefined
    }
    return data.stops.find((s: TripStop) => s.id === stopId)
  }, [stopId, data])

  const daysForStop = useMemo((): Array<TripDay> => {
    return stop?.days ?? []
  }, [stop])

  const itemsWithoutDay = useMemo(() => {
    return stop?.itemsWithoutDay ?? []
  }, [stop])

  const hasItemsWithoutDay = itemsWithoutDay.length > 0
  const hasDays = daysForStop.length > 0

  useEffect(() => {
    recheckDayPillsOverflow()
  }, [daysForStop, recheckDayPillsOverflow])

  const [selectedView, setSelectedView] = useState<number | 'all' | 'to-be-planned'>('all')

  useEffect(() => {
    if (scrollTargetDayId === undefined) return
    const dayExists = daysForStop.some((day: TripDay) => day.id === scrollTargetDayId)
    if (dayExists) {
      setSelectedView(scrollTargetDayId)
    }
  }, [scrollTargetDayId, daysForStop])

  const selectedDay = useMemo(() => {
    if (selectedView === 'all' || typeof selectedView !== 'number') {
      return undefined
    }
    return daysForStop.find((day: TripDay) => day.id === selectedView)
  }, [selectedView, daysForStop])

  const selectedDayItems = useMemo(() => {
    return selectedDay?.items ?? []
  }, [selectedDay])

  // Categorise items for position logic:
  // - ongoingStayItems: condensed cards above time sections
  // - checkInAccommodationItems: placed in time section based on check-in time
  // - regularItems: activities grouped by time period
  const { ongoingStayItems, checkInAccommodationItems, regularItems } = useMemo(() => {
    const ongoing: Array<TripItineraryItem> = []
    const checkInAccom: Array<TripItineraryItem> = []
    const regular: Array<TripItineraryItem> = []
    for (const item of selectedDayItems) {
      const isAccommodation = item.type === 'ACCOMMODATION'
      const role = toStopVisitRole(item.role)
      if (isAccommodation && role === STOP_VISIT_ROLE.STAY && item.isCheckIn === false) {
        ongoing.push(item)
      } else if (isAccommodation) {
        checkInAccom.push(item)
      } else {
        regular.push(item)
      }
    }
    return { ongoingStayItems: ongoing, checkInAccommodationItems: checkInAccom, regularItems: regular }
  }, [selectedDayItems])

  const hasAnyItemsWithTime = useMemo(
    () => regularItems.some(item => Boolean(item.time)) || checkInAccommodationItems.length > 0,
    [regularItems, checkInAccommodationItems],
  )

  const groupedItemsByTime = useMemo(() => {
    const itemsWithTime = regularItems.filter((item): item is TripItineraryItem => Boolean(item.time))
    const grouped = groupBy<TimePeriod, TripItineraryItem>(itemsWithTime, (item) => getTimePeriod(item.time))

    return {
      morning: grouped.get('morning') ?? [],
      afternoon: grouped.get('afternoon') ?? [],
      evening: grouped.get('evening') ?? [],
    }
  }, [regularItems])

  // Activities without a time slot, rendered after time groups
  const itemsWithoutTime = useMemo(() => {
    return regularItems.filter(item => !item.time)
  }, [regularItems])

  const checkInByTimePeriod = useMemo(() => {
    const morning: Array<TripItineraryItem> = []
    const afternoon: Array<TripItineraryItem> = []
    const evening: Array<TripItineraryItem> = []
    for (const item of checkInAccommodationItems) {
      const period = item.time ? getTimePeriod(item.time) : 'afternoon'
      if (period === 'afternoon') {
        afternoon.push(item)
      } else if (period === 'evening') {
        evening.push(item)
      } else {
        morning.push(item)
      }
    }
    return { morning, afternoon, evening }
  }, [checkInAccommodationItems])

  const morningItems = useMemo((): Array<TripItineraryItem> => {
    return [...checkInByTimePeriod.morning, ...groupedItemsByTime.morning]
  }, [checkInByTimePeriod.morning, groupedItemsByTime.morning])

  const afternoonItems = useMemo((): Array<TripItineraryItem> => {
    return [...checkInByTimePeriod.afternoon, ...groupedItemsByTime.afternoon]
  }, [checkInByTimePeriod.afternoon, groupedItemsByTime.afternoon])

  const eveningItems = useMemo((): Array<TripItineraryItem> => {
    return [...checkInByTimePeriod.evening, ...groupedItemsByTime.evening]
  }, [checkInByTimePeriod.evening, groupedItemsByTime.evening])

  const onDayPillClickHandler = useCallback((dayId: number | 'all' | 'to-be-planned') => {
    setSelectedView((prev) => {
      if (prev === dayId) {
        return prev
      }
      return dayId
    })
  }, [])

  const handleAllClick = useCallback(() => {
    onDayPillClickHandler('all')
  }, [onDayPillClickHandler])

  const handleToBePlannedClick = useCallback(() => {
    onDayPillClickHandler('to-be-planned')
  }, [onDayPillClickHandler])

  const dayPillHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}
    daysForStop.forEach((day: TripDay) => {
      handlers[day.id] = () => {
        onDayPillClickHandler(day.id)
      }
    })
    return handlers
  }, [daysForStop, onDayPillClickHandler])

  const getItemsForDay = useCallback((dayId: number) => {
    const day = daysForStop.find(d => d.id === dayId)
    return day?.items ?? []
  }, [daysForStop])

  const handleViewDayItinerary = useCallback((dayId: number) => {
    setSelectedView(dayId)
  }, [])

  const allDays = useMemo(() =>
    daysForStop
      .map(day => ({ day, items: getItemsForDay(day.id) })),
  [daysForStop, getItemsForDay],
  )

  const toBePlannedPreviewImageIds = useMemo(() => getPreviewImages(itemsWithoutDay), [itemsWithoutDay])

  const toBePlannedExtraImageCount = useMemo(() => (
    Math.max(itemsWithoutDay.length - toBePlannedPreviewImageIds.length, 0)
  ), [itemsWithoutDay.length, toBePlannedPreviewImageIds.length])

  const deleteDay = useCallback((dayId: number) => {
    deleteDayMutation.mutate(
      { dayId },
      {
        onSuccess: () => {
          showSnackbar(intl.formatMessage({
            id: '84Wc8rLNkM7gfiHIGr6n',
            defaultMessage: 'Day deleted successfully',
            description: 'Success snackbar message after deleting a day from the trip itinerary',
          }), 'success')
          setSelectedView('all')
          onDayDeleted?.()
        },
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: 'vLCihX2yPmhSFgdhyYtT',
            defaultMessage: 'Failed to delete day. Please try again.',
            description: 'Error snackbar message when deleting a day from the trip itinerary fails',
          }), 'critical')
        },
      },
    )
  }, [deleteDayMutation, onDayDeleted, intl])

  const handleDeleteSelectedDay = useCallback(() => {
    if (selectedDay?.id) {
      deleteDay(selectedDay.id)
    }
  }, [selectedDay?.id, deleteDay])

  const handleDeleteItem = useCallback((itemId: number) => {
    deleteItemMutation.mutate(
      { itemId },
      {
        onSuccess: () => {
          showSnackbar(intl.formatMessage({
            id: 'vdRJZT3jd1OOFSydHWE5',
            defaultMessage: 'Item deleted successfully',
            description: 'Success snackbar message after deleting an item from the trip itinerary',
          }), 'success')
          onItemDeleted?.()
        },
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: 'jbYTzRjZ_kilMyQcouOd',
            defaultMessage: 'Failed to delete item. Please try again.',
            description: 'Error snackbar message when deleting an item from the trip itinerary fails',
          }), 'critical')
        },
      },
    )
  }, [deleteItemMutation, onItemDeleted, intl])

  const allChipLabel = intl.formatMessage({
    id: 'Ed5VmiQiAlafutnGme_3',
    defaultMessage: 'All',
    description: 'Chip label to show all days in the trip itinerary in the trip planner AI chat',
  })

  const toBePlannedLabel = intl.formatMessage({
    id: 'tymxCqShzCm5vqd0-Dlz',
    defaultMessage: 'To be planned',
    description: 'Chip label and heading for items not yet assigned to a day in the trip itinerary',
  })

  const morningHeading = intl.formatMessage({
    id: 'z4paiuuzgrhgzMhtoZcw',
    defaultMessage: 'Morning',
    description: 'Heading for morning time period in the day itinerary',
  })

  const afternoonHeading = intl.formatMessage({
    id: 'ZV9SYhHEFDKtGU36lYXP',
    defaultMessage: 'Afternoon',
    description: 'Heading for afternoon time period in the day itinerary',
  })

  const eveningHeading = intl.formatMessage({
    id: 'EJbXVrC5Lqwu4tHrpv-S',
    defaultMessage: 'Evening',
    description: 'Heading for evening time period in the day itinerary',
  })

  const emptyToBePlannedMessage = intl.formatMessage({
    id: 'k-yjpWZBwHTqQXF7L49H',
    defaultMessage: 'No items to plan yet. Add items to your trip to see them here.',
    description: 'Empty state message when there are no unplanned items in the trip itinerary',
  })

  const scrollLeftAriaLabel = intl.formatMessage({
    id: 'pofK1QUc35zRPjBuRjnr',
    defaultMessage: 'Scroll left',
    description: 'Aria label for the scroll left button on the day pills carousel',
  })

  const scrollRightAriaLabel = intl.formatMessage({
    id: 'bz6ZtcgB1RlsQDjDOl7x',
    defaultMessage: 'Scroll right',
    description: 'Aria label for the scroll right button on the day pills carousel',
  })

  const emptyDayMessage = intl.formatMessage({
    id: 'gSkaW0y1lUiW_IYVyftV',
    defaultMessage: 'No items planned for this day yet.',
    description: 'Empty state message when a trip day has no items planned',
  })

  const askAILabel = intl.formatMessage({
    id: 'LMRNbMot5T2ZYHczoJwD',
    defaultMessage: 'Ask AI',
    description: 'Chip label to ask AI for suggestions about a trip day',
  })

  const handleAskAIClick = useCallback(() => {
    if (!selectedDay) return
    onAskAI?.(selectedDay.dayNumber, selectedDay.title, selectedDay.date)
  }, [onAskAI, selectedDay])

  return (
    <>
      <PaddedContainer ref={containerRef}>
        <Group direction="vertical" gap={20} fullWidth>
          {hasDays && (
            <ScrollIndicatorWrapper>
              {canScrollLeft && (
                <ScrollFade className="left">
                  <IconButton kind="tertiary" shape="circle" size="small" onClick={scrollLeft} aria-label={scrollLeftAriaLabel}>
                    <LineAngleLeftIcon />
                  </IconButton>
                </ScrollFade>
              )}
              <DayPillsScrollContainer ref={dayPillsScrollRef}>
                <Group direction="horizontal" gap={8} verticalAlign="center" wrap="nowrap">
                  <DayChip
                    variant="filled"
                    size="medium"
                    onClick={handleAllClick}
                    className={clsx({ isSelected: selectedView === 'all' })}
                  >
                    {allChipLabel}
                  </DayChip>
                  {daysForStop.map((day: TripDay) => (
                    <DayChip
                      key={day.id}
                      variant="filled"
                      size="medium"
                      className={clsx({ isSelected: selectedView === day.id })}
                      onClick={dayPillHandlers[day.id]}
                    >
                      {getLocalisedDayLabel(intl, day.dayNumber, day.date)}
                    </DayChip>
                  ))}
                  <DayChip
                    variant="filled"
                    size="medium"
                    onClick={handleToBePlannedClick}
                    className={clsx({ isSelected: selectedView === 'to-be-planned' })}
                  >
                    {toBePlannedLabel}
                  </DayChip>
                </Group>
              </DayPillsScrollContainer>
              {canScrollRight && (
                <ScrollFade className="right">
                  <IconButton kind="tertiary" shape="circle" size="small" onClick={scrollRight} aria-label={scrollRightAriaLabel}>
                    <LineAngleRightIcon />
                  </IconButton>
                </ScrollFade>
              )}
            </ScrollIndicatorWrapper>
          )}

          {hasDays && selectedView === 'all' && (
            <FadeContainer key="all">
              <SummaryList>
                {allDays.map(({ day, items }, index) => (
                  <React.Fragment key={day.id}>
                    <ItineraryContent
                      day={day}
                      items={items}
                      onViewDayItinerary={handleViewDayItinerary}
                    />
                    {(index < allDays.length - 1 || hasItemsWithoutDay) && <SummaryDivider kind="secondary" />}
                  </React.Fragment>
                ))}
                {hasItemsWithoutDay && (
                  <ItinerarySummaryRow
                    indicator={<LineCalendarIcon size="S" />}
                    title={toBePlannedLabel}
                    subtitle={intl.formatMessage(
                      {
                        id: 'CFXNPaAAVThQTeKcnVuu',
                        defaultMessage: '{count, plural, one {# activity} other {# activities}}',
                        description: 'Activity count label shown in the condensed to-be-planned itinerary summary row',
                      },
                      { count: itemsWithoutDay.length },
                    )}
                    previewImageIds={toBePlannedPreviewImageIds}
                    extraImageCount={toBePlannedExtraImageCount}
                    onClick={handleToBePlannedClick}
                  />
                )}
              </SummaryList>
            </FadeContainer>
          )}

          {hasDays && selectedDay && (
            <FadeContainer key={`day-${selectedDay.id}`}>
              <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="large" weight="bold">
                      {getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date)}
                    </BodyText>
                    <Heading variant="heading4">{selectedDay.title}</Heading>
                  </Group>
                  <Group direction="horizontal" gap={4}>
                    {onAskAI && (
                      <AskAiChip
                        size="medium"
                        variant="outline"
                        startIcon={<AISparkleIcon size="S" />}
                        onClick={handleAskAIClick}
                      >
                        {askAILabel}
                      </AskAiChip>
                    )}
                    <ChatItemDropdown onDelete={handleDeleteSelectedDay} />
                  </Group>
                </Group>
                {selectedDay.description && (
                  <BodyText variant="medium">{selectedDay.description}</BodyText>
                )}
                {selectedDayItems.length === 0 && (
                  <BodyText variant="medium" colour="neutral-three">
                    {emptyDayMessage}
                  </BodyText>
                )}
                {ongoingStayItems.length > 0 && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <ItemCardList
                      items={ongoingStayItems}
                      dayDate={selectedDay.date}
                      onDelete={handleDeleteItem}
                      onViewOfferDetails={handleViewOfferDetails}
                      onAddToTrip={handleAddToTrip}
                    />
                  </Group>
                )}
                {hasAnyItemsWithTime && (
                  <>
                    {morningItems.length > 0 && (
                      <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                        <BodyText variant="large" weight="bold">{morningHeading}</BodyText>
                        <ItemCardList
                          items={morningItems}
                          dayDate={selectedDay.date}
                          onDelete={handleDeleteItem}
                          onClick={handleItemClick}
                          onViewOfferDetails={handleViewOfferDetails}
                          onAddToTrip={handleAddToTrip}
                        />
                      </Group>
                    )}
                    {afternoonItems.length > 0 && (
                      <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                        <BodyText variant="large" weight="bold">{afternoonHeading}</BodyText>
                        <ItemCardList
                          items={afternoonItems}
                          dayDate={selectedDay.date}
                          onDelete={handleDeleteItem}
                          onClick={handleItemClick}
                          onViewOfferDetails={handleViewOfferDetails}
                          onAddToTrip={handleAddToTrip}
                        />
                      </Group>
                    )}
                    {eveningItems.length > 0 && (
                      <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                        <BodyText variant="large" weight="bold">{eveningHeading}</BodyText>
                        <ItemCardList
                          items={eveningItems}
                          dayDate={selectedDay.date}
                          onDelete={handleDeleteItem}
                          onClick={handleItemClick}
                          onViewOfferDetails={handleViewOfferDetails}
                          onAddToTrip={handleAddToTrip}
                        />
                      </Group>
                    )}
                    {itemsWithoutTime.length > 0 && (
                      <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                        <ItemCardList
                          items={itemsWithoutTime}
                          dayDate={selectedDay.date}
                          onDelete={handleDeleteItem}
                          onClick={handleItemClick}
                          onViewOfferDetails={handleViewOfferDetails}
                          onAddToTrip={handleAddToTrip}
                        />
                      </Group>
                    )}
                  </>
                )}
                {!hasAnyItemsWithTime && (regularItems.length > 0 || checkInAccommodationItems.length > 0) && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <ItemCardList
                      items={[...checkInAccommodationItems, ...regularItems]}
                      dayDate={selectedDay.date}
                      onDelete={handleDeleteItem}
                      onClick={handleItemClick}
                      onViewOfferDetails={handleViewOfferDetails}
                      onAddToTrip={handleAddToTrip}
                    />
                  </Group>
                )}
              </Group>
            </FadeContainer>
          )}

          {hasDays && selectedView === 'to-be-planned' && (
            <FadeContainer key="to-be-planned">
              <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                <Heading variant="heading4">{toBePlannedLabel}</Heading>
                {hasItemsWithoutDay && (
                  <Group direction="vertical" gap={ITEM_CARD_GAP} fullWidth>
                    <ItemCardList
                      items={itemsWithoutDay}
                      onDelete={handleDeleteItem}
                      onClick={handleItemClick}
                      onViewOfferDetails={handleViewOfferDetails}
                      onAddToTrip={handleAddToTrip}
                    />
                  </Group>
                )}
                {!hasItemsWithoutDay && (
                  <BodyText variant="medium" colour="neutral-three">
                    {emptyToBePlannedMessage}
                  </BodyText>
                )}
              </Group>
            </FadeContainer>
          )}
        </Group>
      </PaddedContainer>

      {selectedPlace && (
        <PlaceInfoDrawer
          isOpen
          onClose={handleDrawerClose}
          googlePlaceId={selectedPlace.googlePlaceId}
          placeName={selectedPlace.placeName}
          itemId={selectedPlace.itemId}
          onDelete={handleDeleteItem}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}

      {selectedOffer?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={selectedOffer.offerId}
          isOpen
          onClose={handleCloseHotelDrawer}
          onShowMap={handleShowExperienceOnMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      {selectedOffer && selectedOffer.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={selectedOffer.offerId}
          accommodationPropertyId={selectedOffer.accommodationPropertyId}
          offerType={selectedOffer.offerType}
          isOpen
          onClose={handleCloseHotelDrawer}
          onShowMap={onShowMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
          searchParams={selectedOffer.searchParams}
        />
      )}

      {addToTripItem && data && (
        <AddHotelToTripModal
          isOpen
          hotelName={addToTripItem.name}
          itineraryData={data}
          relevantStop={stop}
          onClose={handleAddToTripModalClose}
          onSubmit={handleAddToTripSubmit}
        />
      )}
    </>
  )
}

export default DayItineraryCanvas
